import {createSelector} from 'reselect';
import {VariableID} from '@packages/systems/core/utils/StyleVariables';
import type {DesignerStores} from '@packages/systems/designer/flux';
import {isVisibleAsset} from '@packages/shared/utils/AssetUtils';
import {isLibraryAsset} from '@packages/domains/assets/utilities';
import {
  selectComponents,
  selectSiteUserComponents,
} from '@packages/utilities/platform-selectors';
import {type DesignerStateType} from '@packages/systems/core/records/DesignerState';
import {
  type InstalledLibraryTagStylesSettings,
  type LibraryTagStylesSettings,
  isAssetExport,
  isExportableTagStyle,
  LIBRARY_STATUS,
  LibraryManifest,
  LIBRARY_PROFILE_STATUS,
  type LoadedLibraries,
  isLibraryOrigin,
  isLibraryWithTagStyles,
} from '@packages/systems/libraries';
import {prebuiltLayoutsLibraryManifest} from '@packages/systems/core/plugins';
import {
  FONT_SOURCE_CUSTOM,
  FONT_SOURCE_GOOGLE_CUSTOM,
  FONT_SOURCE_TYPEKIT,
} from '@packages/systems/core/constants/Fonts';
import {
  getComponentInstOrigin,
  isCodeComponent,
} from '@packages/utilities/platform';
import {type ExpressionElement} from '@packages/systems/wfdl-ast';
import {getPluginName, qualify} from '@packages/systems/wfdl-Qualified';
import {Component, Plugins} from '@packages/systems/wfdl';
import {isBranchPage} from '@packages/shared/utils/PageUtils';
import {
  parseAvailableLibraries,
  isComponentInstanceFromLibrary,
  getLibraryMetadataFromSourceFiles,
} from './utils';
import {LIBRARIES_FETCH_STATE} from './constants';
import {reconstructFoldersFromFolderPaths} from '../utils/assets';
import {LibraryOrphanedComponent} from '../types';
import {getPageNodes} from '@packages/shared/utils/PageTreeUtils';
import {StyleBlock} from '@packages/systems/style-legacy/records';
import type {AgentInstructionLibraryMetadata} from '@packages/domains/instructions/data-types';

export const selectLibrariesInformation = createSelector(
  (state: DesignerStores) => state.DesignerStore.libraries,
  (state: DesignerStores) =>
    state.DesignerStore.availableLibraries.installations,
  (state: DesignerStores) =>
    state.DesignerStore.availableLibraries.allLibraries,
  (state: DesignerStores) => state.DesignerStore.availableLibraries.fetchStatus,
  (libraries, installations, allLibraries, fetchStatus) => {
    return parseAvailableLibraries(
      installations,
      libraries,
      allLibraries,
      fetchStatus === LIBRARIES_FETCH_STATE.succeeded
    );
  }
);

const isInstalledLibrary = ({status, profileId}: LibraryManifest<string>) =>
  // Every other status means the library is
  // either installed or installing / uninstalling
  status !== LIBRARY_STATUS.install_available &&
  //Avoid sample libraries
  profileId.length > 0;

export const selectIsSiteWithLibraries = createSelector(
  selectLibrariesInformation,
  (libraries) => {
    return libraries.some(isInstalledLibrary);
  }
);

/**
 * Whether the site has an installed library that is not a code library.
 *
 * Only those block sharing: a code library ships nothing but code components,
 * so it leaves no resource of its own in what gets shared.
 */
export const selectIsSiteWithNonCodeLibraryInstalled = createSelector(
  selectLibrariesInformation,
  (libraries) => {
    return libraries.some(
      (library) =>
        isInstalledLibrary(library) && library.type !== 'CODE_LIBRARY'
    );
  }
);

export const selectIsSiteWithPublishedLibrary = createSelector(
  (state: DesignerStores) =>
    state.DesignerStore.availableLibraries.latestSiteLibrary,
  (siteLibrary) => {
    const hasPreviouslyPublishedLibrary =
      siteLibrary !== null && siteLibrary.profileStatus === 'SHARED';

    return hasPreviouslyPublishedLibrary;
  }
);

export const selectIsSiteWithBranchPages = createSelector(
  (state: DesignerStores) => state.PageStore,
  (pageState) => getPageNodes(pageState).toArray().some(isBranchPage)
);

const selectSiteInformationVariables = createSelector(
  (state: DesignerStores) => state.CssVariablesStore.variables,
  (variables) => {
    return variables.filter(
      (variable) => !variable.deleted && !variable.origin
    );
  }
);

const selectSiteInformationAssets = createSelector(
  (state: DesignerStores) => state.AssetStore.assets,
  (assets) => {
    return assets.filter(
      (asset) =>
        isVisibleAsset(asset) && !isLibraryAsset(asset) && isAssetExport(asset)
    );
  }
);

const selectSiteInformationTagStylesCount = createSelector(
  (state: DesignerStores) => state.StyleBlockStore.styleBlocks,
  (styleBlocks) => {
    let count = 0;
    styleBlocks.forEach((block: StyleBlock) => {
      if (!isExportableTagStyle(block)) {
        return;
      }
      count++;
    });
    return count;
  }
);

// Count fonts the user added to this site, by exact family name so the same
// family coming from multiple sources counts once, matching the library
// export identity. Excludes the designer's default picker fonts (web-safe
// and Webflow's curated Google list) and any library-installed font (one
// with an `origin`).
const FONT_SOURCES_THAT_COUNT = new Set<string>([
  FONT_SOURCE_GOOGLE_CUSTOM,
  FONT_SOURCE_CUSTOM,
  FONT_SOURCE_TYPEKIT,
]);
export const selectSiteInformationFontsCount = createSelector(
  (state: DesignerStores) => state.DesignerStore.fonts,
  (fonts) => {
    const families = new Set<string>();
    for (const font of fonts ?? []) {
      if (!font.family) {
        continue;
      }

      if (FONT_SOURCES_THAT_COUNT.has(font.source) && !font.origin) {
        families.add(font.family);
      }
    }
    return families.size;
  }
);

export const selectSiteHasTagStyleBlocks = createSelector(
  (state: DesignerStores) => state.StyleBlockStore.styleBlocks,
  (styleBlocks) => styleBlocks.some(isExportableTagStyle)
);

export const selectLibraryTagStylesSettings = (
  state: DesignerStores
): LibraryTagStylesSettings[] => state.SiteDataStore.libraryTagStylesSettings;

// SourceFilesStore changes identity on every sandbox file commit, but the
// derived counts rarely do — return the previous result reference when they
// are unchanged so selectSiteInformation (and its always-mounted
// subscribers, e.g. the Libraries sidebar icon) doesn't recompute.
let lastInstructionLibraryMetadata: AgentInstructionLibraryMetadata | null =
  null;
const selectInstructionLibraryMetadata = createSelector(
  (state: DesignerStores) => state.SourceFilesStore,
  (sourceFilesState) => {
    const next = getLibraryMetadataFromSourceFiles(
      Object.values(sourceFilesState)
    );
    if (
      lastInstructionLibraryMetadata !== null &&
      lastInstructionLibraryMetadata.sourceFilesCount ===
        next.sourceFilesCount &&
      lastInstructionLibraryMetadata.skillsCount === next.skillsCount &&
      lastInstructionLibraryMetadata.rulesCount === next.rulesCount
    ) {
      return lastInstructionLibraryMetadata;
    }
    lastInstructionLibraryMetadata = next;
    return next;
  }
);

export const selectSiteInformation = createSelector(
  (state: DesignerStores) => state.SiteDataStore.siteName,
  (state: DesignerStores) => state.SiteDataStore.isCloneable,
  (state: DesignerStores) => state.SiteDataStore.workspaceName,
  (state: DesignerStores) => state.SiteDataStore.workspaceSlug,
  selectSiteInformationVariables,
  selectSiteUserComponents,
  selectSiteInformationAssets,
  selectSiteInformationTagStylesCount,
  selectSiteInformationFontsCount,
  selectInstructionLibraryMetadata,
  (
    siteName,
    isCloneable,
    workspaceName,
    workspaceSlug,
    variablesList,
    componentMap,
    assetsList,
    tagStylesCount,
    fontsCount,
    instructions
  ) => {
    const {componentsCount, codeComponentsCount} = componentMap.reduce(
      (acc, _component, componentName) => {
        if (isCodeComponent(componentMap)(componentName)) {
          acc.codeComponentsCount++;
        } else {
          acc.componentsCount++;
        }
        return acc;
      },
      {componentsCount: 0, codeComponentsCount: 0}
    );
    return {
      siteName,
      isCloneable,
      workspaceName: workspaceName || '',
      workspaceSlug: workspaceSlug || '',
      componentsCount,
      codeComponentsCount,
      variablesCount: variablesList.count(),
      assetsCount: assetsList.count(),
      tagStylesCount,
      fontsCount,
      sourceFilesCount: instructions?.sourceFilesCount ?? 0,
      skillsCount: instructions?.skillsCount ?? 0,
      rulesCount: instructions?.rulesCount ?? 0,
    };
  }
);

export const selectLatestSiteLibrary = (state: DesignerStores) =>
  state.DesignerStore.availableLibraries.latestSiteLibrary;

export const selectLocalLibraryInstallations = (state: DesignerStores) =>
  state.DesignerStore.localLibrary.libraryInstallations;

export const selectOutdatedInstallationCount = createSelector(
  selectLocalLibraryInstallations,
  selectLatestSiteLibrary,
  (libraryInstallations, latestSiteLibrary) => {
    if (!libraryInstallations || !latestSiteLibrary?.pluginId) return 0;

    return libraryInstallations.filter(
      (installation) =>
        installation.libraryVersion.id !== latestSiteLibrary.pluginId
    ).length;
  }
);

export const selectLibrariesWithUpdates = createSelector(
  selectLibrariesInformation,
  (state: DesignerStores) =>
    state.EntitlementsStore.entitlements.allowCodeComponents,
  (libraries, allowCodeComponentsEntitlement) => {
    const libraryUpdates = libraries.filter((library) => {
      if (
        library?.type === 'CODE_LIBRARY' &&
        !allowCodeComponentsEntitlement?.hasAccess
      ) {
        return false;
      }

      return (
        Boolean(library.updatedLibraryVersion) &&
        library.status === LIBRARY_STATUS.upgrade_available &&
        library.profileStatus === LIBRARY_PROFILE_STATUS.SHARED
      );
    });

    return libraryUpdates.length === libraries.length
      ? libraries
      : libraryUpdates;
  }
);

/**
 * Select loaded (i.e. installed) libraries
 */
export const selectLoadedLibraries: (state: {
  DesignerStore: DesignerStateType;
}) => LoadedLibraries = (state) => state.DesignerStore.libraries;

/**
 * Installed workspace libraries that export at least one tag style —
 * the set of libraries the "Manage tag styles" panel applies to.
 */
export const selectInstalledLibrariesWithTagStyles = createSelector(
  selectLoadedLibraries,
  (libraries): LibraryManifest<string>[] => {
    const filtered = Object.entries(libraries).reduce<
      LibraryManifest<string>[]
    >((acc, [pluginId, lib]) => {
      // Skip the built-in PrebuiltLayouts entry — it lives in
      // `defaultLoadedLibrariesState` and isn't a user-installed library.
      if (pluginId === prebuiltLayoutsLibraryManifest.pluginId) return acc;
      if (!isLibraryWithTagStyles(lib)) return acc;
      acc.push(lib);
      return acc;
    }, []);
    return filtered.sort((a, b) => a.displayName.localeCompare(b.displayName));
  }
);

/**
 * Plugin ids of every installed library, including ones whose plugin is still
 * loading or failed to load. Library content from any other plugin is orphaned.
 */
export const selectInstalledLibraryPluginIds = createSelector(
  (state: {DesignerStore: DesignerStateType}) =>
    state.DesignerStore.availableLibraries.installations,
  selectLoadedLibraries,
  (state: {DesignerStore: DesignerStateType}) =>
    state.DesignerStore.dynamicPluginsManager.pluginDefinitions,
  (installations, libraries, pluginDefinitions): ReadonlySet<string> => {
    const pluginIds = new Set<string>(Object.keys(installations));
    for (const pluginId in libraries) pluginIds.add(pluginId);
    for (const pluginDefinition of pluginDefinitions) {
      pluginIds.add(pluginDefinition.value.id);
    }
    return pluginIds;
  }
);

export const selectInstalledLibrariesTagStylesSettings = (
  state: Pick<DesignerStores, 'StyleBlockStore'>
): InstalledLibraryTagStylesSettings =>
  state.StyleBlockStore.installedLibrariesTagStylesSettings;

/**
 * Returns a function that given a component instance, determines if it originates
 * from a library.
 */
export const selectIsComponentInstanceFromLibrary = createSelector(
  selectLoadedLibraries,
  selectComponents,
  (libraries, componentMap) =>
    (componentInstance: ExpressionElement): boolean =>
      isComponentInstanceFromLibrary({
        libraries,
        componentMap,
        componentInstance,
      })
);

export const selectIsComponentFromLibrary = createSelector(
  selectLoadedLibraries,
  selectComponents,
  (libraries, components) =>
    (componentId: string): boolean => {
      const component = components.get(
        qualify(Plugins.name('__SitePlugin'), Component.name(componentId))
      );
      if (!component) return false;
      const origin = Component.getOrigin(component) ?? null;
      return isLibraryOrigin(origin, libraries);
    }
);

/**
 * Returns a function that given a component instance, determines the origin library.
 */
export const selectComponentInstLibrary = createSelector(
  selectLoadedLibraries,
  selectComponents,
  (libraries, componentMap) =>
    (
      componentInstance: ExpressionElement
    ): LibraryManifest<string> | undefined => {
      const origin = getComponentInstOrigin(componentMap)(componentInstance);
      if (!origin) {
        return undefined;
      }
      return libraries[getPluginName(origin)];
    }
);

/**
 * Selects components that originated from workspace libraries but are now orphaned
 */
export const selectLibraryOrphanedComponents = createSelector(
  selectLoadedLibraries,
  selectComponents,
  (libraries, componentMap): LibraryOrphanedComponent[] => {
    return componentMap.reduceAtPluginName(
      '__SitePlugin',
      (acc, component, componentName) => {
        const origin = Component.getOrigin(component);
        if (!origin) {
          return acc;
        }
        const originPlugin = getPluginName(origin);
        if (originPlugin === 'Basic') {
          // There was a bug with Basic::LineBreak plugin components
          // being treated as library components. The bug has been fixed
          // but we still need to run a migration, so there are production sites
          // with LineBreak components with an origin. We should exclude them from here.
          return acc;
        }

        if (!libraries[originPlugin]) {
          const componentType = qualify(
            Plugins.name('__SitePlugin'),
            componentName
          );
          acc.push({componentType, component});
        }

        return acc;
      },
      [] as LibraryOrphanedComponent[]
    );
  }
);

/**
 * Selects variables that originated from workspace libraries but are now orphaned
 */
export const selectLibraryOrphanedVariables = createSelector(
  selectLoadedLibraries,
  (state: DesignerStores) => state.CssVariablesStore.variables,
  (libraries, variableMap): VariableID[] => {
    return variableMap.reduce((acc, variable, variableId) => {
      const origin = variable.origin;
      if (!origin) {
        return acc;
      }
      const originPlugin = getPluginName(origin);

      if (!libraries[originPlugin]) {
        acc.push(variableId);
      }

      return acc;
    }, [] as VariableID[]);
  }
);

export const selectLibraryFolders = createSelector(
  selectLoadedLibraries,
  (state: DesignerStores) => state.DesignerStore.plugins,
  (libraries, plugins) => {
    // Filter and transform libraries into folder structures
    // Only include libraries that:
    // 1. Have at least one asset export
    // 2. Have corresponding plugin assets registered
    const libraryFolders = Object.values(libraries)
      .filter(
        (library) =>
          library.exports.some((exp) => exp.type === 'Asset') &&
          plugins.assets.byPlugin()[library.pluginId]
      )
      .flatMap((library) => {
        // Generate sub-folders based on asset folder paths
        // This creates the nested folder structure for the library
        return reconstructFoldersFromFolderPaths({
          pluginAssets: Object.values(
            plugins.assets.byPlugin()[library.pluginId] ?? {}
          ),
          pluginId: library.pluginId,
          exports: library.exports,
          datePublished: library.datePublished,
        });
      });

    const sortedFolders = libraryFolders.sort((folder1, folder2) =>
      folder1.name.localeCompare(folder2.name)
    );

    return sortedFolders;
  }
);

export const selectLibrariesWithAssets = createSelector(
  selectLoadedLibraries,
  (state: DesignerStores) => state.DesignerStore.plugins,
  (libraries, plugins) => {
    const hasAssets = Object.values(libraries).filter(
      (library) =>
        library.exports.some((exp) => exp.type === 'Asset') &&
        plugins.assets.byPlugin()[library.pluginId]
    );

    return hasAssets;
  }
);

/**
 * Selects if user is installing, updating, or uninstalling a library.
 */
export const selectIsManageLibraryActionPending = createSelector(
  (state: DesignerStores) =>
    state.DesignerStore.availableLibraries.installations,
  (state: DesignerStores) =>
    state.DesignerStore.availableLibraries.manageLibraryAction,
  (installations, manageLibraryAction) => {
    if (manageLibraryAction?.status === 'pending') {
      return true;
    }

    for (const installation of Object.values(installations)) {
      if (
        installation.installationStatus === LIBRARY_STATUS.installing ||
        installation.installationStatus === LIBRARY_STATUS.uninstalling
      ) {
        return true;
      }
    }

    return false;
  }
);

/**
 * Determines if any library operation is currently in progress.
 *
 * @returns {boolean} Returns true if:
 *   - The dynamic plugins manager is actively running (e.g., fetching a plugin)
 *   - OR there are any library operations in progress (installations, updates, or uninstalls)
 *   - OR there are any plugins still being loaded (e.g. not present in the store)
 */
export const selectIsLibraryOperationRunning = createSelector(
  selectIsManageLibraryActionPending,
  selectLoadedLibraries,
  (state: DesignerStores) => state.DesignerStore.dynamicPluginsManager,
  (isManageLibraryActionPending, libraries, dynamicPluginsManager) => {
    // Check if the user is installing, updating, or uninstalling a library
    if (isManageLibraryActionPending) {
      return true;
    }

    // Check if a plugin might be being fetched
    if (dynamicPluginsManager.status !== 'IDLE') {
      return true;
    }

    // Check if all plugins are loaded
    return dynamicPluginsManager.pluginDefinitions.some(
      (pluginDefinition) =>
        pluginDefinition.status === 'OK' &&
        !libraries[pluginDefinition.value.id]
    );
  }
);
